<template>
    <div class="tabs-panel w-full">
        <slot
            :tabs="tabs"
            :select-tab="selectTab"
            :active-tab="activeTab"
            name="header"
        >
            <div :class="panelListClass">
                <div
                    v-show="!hideTabs"
                    ref="panel"
                    class="tab-panel__container flex overflow-x-scroll relative tab-panel w-full md:w-auto"
                >
                    <ul class="flex max-md:container space-x-8">
                        <li
                            v-for="tab in tabs"
                            :key="tab.id"
                            class="inline tab-panel__tab"
                            :data-testid="tab.testId"
                        >
                            <span
                                v-if="!tab.progress"
                                :ref="tab.id"
                                :class="[
                                    tab === activeTab ? 'opacity-100' : 'opacity-20 hover:opacity-50',
                                    'font-medium inline-flex pb-3 text-2xl cursor-pointer whitespace-nowrap',
                                ]"
                                @click="selectTab(tab.id)"
                                @mouseover="animateTabBorder(tab)"
                                @mouseleave="resetTabBorder"
                            >
                                <i
                                    v-if="tab.icon"
                                    :data-testid="tab.iconTestId"
                                    :class="[`icon icon-${tab.icon} mr-1.5`]"
                                />
                                <span>{{ tab.name }}</span>
                            </span>
                            <Skeleton
                                v-else
                                appearance="tab"
                            ></Skeleton>
                        </li>
                    </ul>

                    <span
                        :style="animatedBorderStyle"
                        class="tab-panel__animated-border"
                    ></span>
                </div>
            </div>
        </slot>
        <slot></slot>
    </div>
</template>

<script>
import { debounce } from 'lodash-es';
import TabsPanelMixin from './TabsPanelMixin';

export default {
    name: 'TabsPanel',
    extends: TabsPanelMixin,
    props: {
        noUrl: {
            type: Boolean,
            default: false,
        },
        hideTabs: {
            type: Boolean,
            default: false,
        },
        panelListClass: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        animatedBorderStyle: null,
        animatedTab: null,
    }),
    computed: {
        urlTabId() {
            return (!this.noUrl && window.location?.hash?.substr(1)) || null;
        },
    },
    watch: {
        activeTab(tab) {
            if (!this.noUrl) {
                window.location.hash = `#${tab.id}`;
            }
            this.resetTabBorder();
        },
        async hideTabs(value) {
            if (value === false) {
                await this.$nextTick();
                this.resetTabBorder();
            }
        },
        tabs: {
            deep: true,
            async handler() {
                // wait a tick so tab changes have time to render
                await this.$nextTick();
                // recalculate animated border style
                this.calculateAnimatedBorderStyle();
            },
        },
    },
    created() {
        this.calculateAnimatedBorderStyleDebounced = debounce(this.calculateAnimatedBorderStyle, 150);
        window.addEventListener('resize', this.calculateAnimatedBorderStyleDebounced);
    },
    mounted() {
        this.resetTabBorder();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.calculateAnimatedBorderStyleDebounced);
    },
    methods: {
        calculateAnimatedBorderStyle() {
            const tabId = this.animatedTab?.id;
            if (!tabId) {
                return;
            }
            const animatedTab = this.$refs[tabId]?.[0];
            if (!animatedTab) {
                return;
            }
            const computedStyle = window.getComputedStyle(animatedTab);
            const paddingLeft = parseFloat(computedStyle.paddingLeft);
            const paddingRight = parseFloat(computedStyle.paddingRight);

            const innerContentLeft = animatedTab.offsetLeft + paddingLeft;
            const innerContentWidth = animatedTab.offsetWidth - paddingLeft - paddingRight;

            this.animatedBorderStyle = {
                left: `${innerContentLeft}px`,
                width: `${innerContentWidth}px`,
            };
        },
        animateTabBorder(tab) {
            this.animatedTab = tab;
            this.calculateAnimatedBorderStyle();
        },
        resetTabBorder() {
            this.animatedTab = this.activeTab;
            this.calculateAnimatedBorderStyle();
        },
    },
};
</script>

<style lang="scss" scoped>
.tab-panel {
    /*// Firefox*/
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.tab-panel__animated-border {
    background-color: theme('colors.blue.600');
    bottom: 0;
    height: 4px;
    position: absolute;
    transition: all 0.3s cubic-bezier(0.785, 0.135, 0.15, 0.86);
}
</style>
